import React from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import {
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledPeoplePicker from '../../../components/ControlledPeoplePicker';
import ControlledAirportSelect from '../../../components/ControlledAirportSelect';

import { SafetyEventReport, Employee, EmployeePosition, FormGenerator } from '../../../core/api/clients';
import { useDispatchEventFieldOptions } from '../../../core/context/DispatchEventFieldOptionsContext';
import { useSafetyEventReportData } from '../../../core/context/SafetyEventReportDataContext';
import ControlledAircraftSelect from '../../../components/ControlledAircraftSelect';

import { transformToUtcTime } from '../../../core/textTransforms';
import ControlledSelect from '../../../components/ControlledSelect';

function FlightStep({ visible, readOnly, isRevenue }:
{
  visible: boolean,
  readOnly?: boolean,
  isRevenue?: boolean,
}) {
  const { airports, aircrafts } = useDispatchEventFieldOptions();
  const { safetyEventReport, allowedActions } = useSafetyEventReportData();
  const { control, setValue } = useFormContext<SafetyEventReport>();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <ControlledTextField<SafetyEventReport>
            name="flightNumber"
            control={control}
            rules={{ required: safetyEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit || safetyEventReport.formGeneratedFrom === FormGenerator.FlightLookup}
            TextFieldProps={{ disabled: safetyEventReport.formGeneratedFrom === FormGenerator.FlightLookup }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledSelect<SafetyEventReport>
            name="mainlinePartner"
            control={control}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
            menuItems={[
              { name: 'AA', value: 'AA' },
              { name: 'ZW', value: 'ZW' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledAircraftSelect<SafetyEventReport>
            name="aircraftNumber"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={aircrafts ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<SafetyEventReport>
            name="departureAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit || safetyEventReport.formGeneratedFrom === FormGenerator.FlightLookup}
            isEditable={safetyEventReport.formGeneratedFrom === FormGenerator.Blank}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<SafetyEventReport>
            name="scheduledArrivalAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit || safetyEventReport.formGeneratedFrom === FormGenerator.FlightLookup}
            isEditable={safetyEventReport.formGeneratedFrom === FormGenerator.Blank}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ControlledAirportSelect<SafetyEventReport>
            name="landingAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        {safetyEventReport.pushTime && (
          <React.Fragment>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">OUT: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(safetyEventReport.pushTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">OFF: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(safetyEventReport.offTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">ON: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(safetyEventReport.onTime)}</Typography>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <Typography variant="caption">IN: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(safetyEventReport.inTime)}</Typography>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledPeoplePicker<SafetyEventReport>
            groupName="captain"
            name="captain"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['captainId', 'captain', 'captainDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('captain', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: safetyEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<SafetyEventReport>
            groupName="firstOfficer"
            name="firstOfficer"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['firstOfficerId', 'firstOfficer', 'firstOfficerDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('firstOfficer', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: safetyEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<SafetyEventReport>
            groupName="flightAttendant"
            name="flightAttendant"
            position={EmployeePosition.InFlight}
            control={control}
            fieldNames={['flightAttendantId', 'flightAttendant', 'flightAttendantDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('flightAttendant', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: isRevenue && safetyEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default FlightStep;
